/**
 * AI Behavior Settings Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * AI personality, language, and custom instructions.
 */

import React from 'react';
import { MessageSquare, AlertTriangle } from 'lucide-react';
import type { AISettingsUpdateData, AIPersonality } from '../../../types/admin';
import {
  AI_PERSONALITY_LABELS,
  AI_PERSONALITY_DESCRIPTIONS,
  AI_LANGUAGE_OPTIONS,
} from '../../../types/admin';
import { aiSettingsService } from '../../../services/admin';

interface AIBehaviorSettingsProps {
  formData: AISettingsUpdateData;
  onChange: (updates: Partial<AISettingsUpdateData>) => void;
}

const PERSONALITIES: AIPersonality[] = ['professional', 'friendly', 'formal'];

export const AIBehaviorSettings: React.FC<AIBehaviorSettingsProps> = ({ formData, onChange }) => {
  const customInstructions = formData.custom_instructions || '';
  const validation = aiSettingsService.validateCustomInstructions(customInstructions);
  const tokenEstimate = aiSettingsService.estimateTokens(customInstructions);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">AI Behavior</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure how the AI assistant communicates with users.
        </p>
      </div>

      {/* Personality */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Communication Style</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PERSONALITIES.map((personality) => (
            <label
              key={personality}
              className={`
                p-4 rounded-lg border cursor-pointer transition-colors
                ${formData.ai_personality === personality ? 'border-[#002B45] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
              `}
            >
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="radio"
                  name="ai_personality"
                  value={personality}
                  checked={formData.ai_personality === personality}
                  onChange={() => onChange({ ai_personality: personality })}
                  className="text-[#002B45] focus:ring-[#F7B500]"
                />
                <span className="font-medium text-gray-900">
                  {AI_PERSONALITY_LABELS[personality]}
                </span>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                {AI_PERSONALITY_DESCRIPTIONS[personality]}
              </p>
            </label>
          ))}
        </div>
      </div>

      {/* Language */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Response Language</label>
        <select
          value={formData.ai_language || 'en'}
          onChange={(e) => onChange({ ai_language: e.target.value })}
          className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
        >
          {AI_LANGUAGE_OPTIONS.map((lang) => (
            <option key={lang.value} value={lang.value}>
              {lang.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          The AI will respond in this language by default.
        </p>
      </div>

      {/* Custom Instructions */}
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">Custom Instructions</label>
          <span className="text-xs text-gray-400">~{tokenEstimate} tokens</span>
        </div>
        <textarea
          value={customInstructions}
          onChange={(e) => onChange({ custom_instructions: e.target.value || undefined })}
          placeholder="Add custom instructions for the AI assistant (e.g., 'Always greet users by name when known')..."
          rows={4}
          maxLength={5000}
          className={`
            w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2
            ${validation.valid ? 'border-gray-300 focus:ring-[#F7B500]' : 'border-red-300 focus:ring-red-500'}
          `}
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-500">{customInstructions.length}/5000 characters</p>
          {!validation.valid && (
            <div className="flex items-center gap-1 text-red-600">
              <AlertTriangle size={12} />
              <span className="text-xs">{validation.warnings[0]}</span>
            </div>
          )}
        </div>
      </div>

      {/* Example Prompts */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <MessageSquare size={16} />
          Example Custom Instructions
        </h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>- "Always address the user by their first name when available"</li>
          <li>- "Include relevant Swiss regulatory references when discussing compliance"</li>
          <li>- "Keep responses concise, under 200 words unless more detail is requested"</li>
          <li>- "When discussing portfolios, always mention risk levels"</li>
        </ul>
      </div>

      {/* Security Note */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Security Note</p>
            <p className="text-xs mt-1">
              Custom instructions are validated for potentially harmful patterns. Instructions that
              attempt to bypass safety measures or manipulate the AI's core behavior will be
              blocked.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIBehaviorSettings;
